import React, { useState } from 'react';
import { ActionSheet, Button, DotLoading, NavBar, SpinLoading } from 'antd-mobile';
import type { Action } from 'antd-mobile/es/components/action-sheet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ForwardSheet } from '../../../components/ForwardSheet';
import { MyWhatsAppAccountBinding, WhatsAppConnectionState } from '../../../api/whatsapp';
import { normalizeMediaUrl } from '../../../utils/media';
import { campaignDetailPageStyles } from './styles';
import { CampaignDetailState, CampaignSendBatchItem, CampaignTemplateCard } from './types';
import { CampaignUserPromotionStatus } from '../CampaignList/types';

interface CampaignDetailViewProps {
  state: CampaignDetailState;
  templates: CampaignTemplateCard[];
  selectedBinding: MyWhatsAppAccountBinding | null;
  onForward: (content: string, mediaUrl?: string) => void;
  onCloseForward: () => void;
  onForwardSubmitted: () => void;
  onSelectBinding: (bindingId: string) => void;
  onCancelSendBatch: (batchId: string) => void;
}

export const CampaignDetailView: React.FC<CampaignDetailViewProps> = ({
  state,
  templates,
  selectedBinding,
  onForward,
  onCloseForward,
  onForwardSubmitted,
  onSelectBinding,
  onCancelSendBatch,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['campaigns', 'forward']);
  const [accountPickerVisible, setAccountPickerVisible] = useState(false);

  // Format ISO date string into a localized human-readable string.
  const formatTime = (value?: string | null): string => {
    if (!value) {
      return '-';
    }
    return new Date(value).toLocaleString();
  };

  if (state.loading) {
    return (
      <div className="webview-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <DotLoading />
      </div>
    );
  }

  if (!state.campaign) {
    return (
      <div className="webview-page">
        <NavBar onBack={() => navigate('/campaigns')}>{t('detail.navTitle')}</NavBar>
        <div className="webview-card">
          <div style={{ textAlign: 'center', color: '#8f95a3', padding: '32px 0' }}>{t('detail.notFound')}</div>
        </div>
      </div>
    );
  }

  const campaign = state.campaign;
  const promotionStatus: CampaignUserPromotionStatus = campaign.user_promotion_status || 'available';
  const usedCount = campaign.user_used_promotion_count ?? 0;
  const promotionLimit = campaign.wa_send_round_limit_per_account || campaign.promotion_limit || 1;
  const batchSize = campaign.wa_daily_send_limit_per_account || 1;
  const remainingCount = Math.max(promotionLimit - usedCount, 0);
  const progressPercent = Math.max(0, Math.min(100, Math.round((usedCount / promotionLimit) * 100)));
  const isBindingVerified = Boolean(selectedBinding?.verified_at);
  const connectionStatus = state.connectionState?.status || 'not_initialized';
  const isConnectionReady = connectionStatus === 'ready' || connectionStatus === 'connected';
  const activeBatch = state.sendBatches.find((batch) => isActiveBatchStatus(batch.status)) || null;
  const canForward = promotionStatus === 'available' && isBindingVerified && isConnectionReady && !activeBatch;
  const selectedAccountLabel = selectedBinding?.account.phone_number || t('campaigns:detail.noAccountSelected');

  const promotionSummary = (() => {
    if (promotionStatus === 'completed') {
      return {
        hint: t('detail.completedHint', { used: usedCount, limit: promotionLimit }),
        buttonText: t('detail.completedAction'),
      };
    }

    if (promotionStatus === 'quota_exhausted') {
      return {
        hint: t('detail.exhaustedHint', { used: usedCount, limit: promotionLimit }),
        buttonText: t('detail.exhaustedAction'),
      };
    }

    return {
      hint: t('detail.availableHint', { used: usedCount, limit: promotionLimit, remaining: remainingCount, batchSize }),
      buttonText: t('detail.forwardNow'),
    };
  })();

  const sendButtonText = (() => {
    if (promotionStatus !== 'available') {
      return promotionSummary.buttonText;
    }
    if (!selectedBinding || !isBindingVerified || !isConnectionReady) {
      return t('detail.accountBlockedAction');
    }
    if (activeBatch) {
      return t('detail.batchBlockedAction');
    }
    return t('detail.forwardNow');
  })();

  const accountStatusHint = (() => {
    if (!selectedBinding) {
      return t('detail.noAccountBoundHint');
    }
    if (!isBindingVerified) {
      return t('detail.accountUnverifiedHint');
    }
    if (state.connectionStateError) {
      return state.connectionStateError;
    }
    if (isConnectionReady) {
      return t('detail.accountReadyHint');
    }
    if (isConnectionLoadingStatus(connectionStatus)) {
      return t('detail.accountConnectingHint');
    }
    if (state.connectionState?.message) {
      return state.connectionState.message;
    }
    return t('detail.accountNotReadyHint');
  })();

  const connectionTag = (() => {
    if (isConnectionReady) {
      return {
        text: t('detail.connectionReadyTag'),
        style: campaignDetailPageStyles.connectionTagReady,
      };
    }
    if (isConnectionLoadingStatus(connectionStatus) || state.connectionStateLoading) {
      return {
        text: t('detail.connectionLoadingTag'),
        style: campaignDetailPageStyles.connectionTagLoading,
      };
    }
    return {
      text: t('detail.connectionBlockedTag'),
      style: campaignDetailPageStyles.connectionTagBlocked,
    };
  })();

  const campaignStatusTag = (() => {
    if (promotionStatus === 'completed') {
      return t('detail.campaignCompletedTag');
    }
    if (promotionStatus === 'quota_exhausted') {
      return t('detail.campaignInProgressTag');
    }
    return t('detail.campaignPendingTag');
  })();

  const accountActions: Action[] = state.accountBindings.map((binding) => ({
    key: binding.id,
    text: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 16, display: 'inline-block', textAlign: 'center' }}>
          {binding.id === selectedBinding?.id ? '✓' : ''}
        </span>
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 700 }}>{binding.account.phone_number}</span>
          <span style={{ fontSize: 12, color: '#7f879b' }}>
            {binding.alias || (binding.verified_at ? t('forward:gating.verifiedTag') : t('forward:gating.unverifiedTag'))}
          </span>
        </span>
      </span>
    ),
    bold: binding.id === selectedBinding?.id,
    onClick: () => {
      setAccountPickerVisible(false);
      onSelectBinding(binding.id);
    },
  }));

  const handleGoConnect = () => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    const connectQuery = selectedBinding?.id
      ? `?connect=${encodeURIComponent(selectedBinding.id)}&returnTo=${returnTo}`
      : `?returnTo=${returnTo}`;
    navigate(`/profile/whatsapp-accounts${connectQuery}`);
  };

  return (
    <div className="webview-page">
      <NavBar onBack={() => navigate('/campaigns')}>{t('detail.navTitle')}</NavBar>
      <div style={campaignDetailPageStyles.topDivider} />

      <ActionSheet
        visible={accountPickerVisible}
        actions={accountActions}
        cancelText={t('forward:switchAccountCancel')}
        onClose={() => setAccountPickerVisible(false)}
      />

      <div className="webview-section">
        <div style={campaignDetailPageStyles.sectionTitleRow}>
          <span style={campaignDetailPageStyles.sectionTitleBar} />
          <span style={campaignDetailPageStyles.sectionTitle}>{t('detail.accountStatusTitle')}</span>
        </div>
        <div style={campaignDetailPageStyles.sectionCard}>
          <div style={campaignDetailPageStyles.headerTags}>
            <span style={campaignDetailPageStyles.headerTag}>{campaignStatusTag}</span>
            <span style={campaignDetailPageStyles.headerTag}>{promotionStatus === 'available' ? t('detail.accountPendingTag') : promotionSummary.buttonText}</span>
            <span
              style={{
                ...campaignDetailPageStyles.verifiedTag,
                ...(isBindingVerified ? campaignDetailPageStyles.verifiedTagSuccess : campaignDetailPageStyles.verifiedTagMuted),
              }}
            >
              {isBindingVerified ? t('detail.verifiedUpperTag') : t('detail.unverifiedUpperTag')}
            </span>
          </div>

          <div style={campaignDetailPageStyles.accountPanel}>
            <div style={campaignDetailPageStyles.accountAvatar}>
              <WhatsAppBadgeGlyph />
            </div>
            <div style={campaignDetailPageStyles.accountContent}>
              <div style={campaignDetailPageStyles.accountHeaderLabel}>{t('forward:accountLabel')}</div>
              <div style={campaignDetailPageStyles.accountHeaderValue}>{selectedAccountLabel}</div>
              <div style={campaignDetailPageStyles.accountAlias}>
                {selectedBinding?.alias || t('detail.defaultAlias')}
              </div>
            </div>
            {state.accountBindings.length > 1 ? (
              <Button
                size="small"
                fill="outline"
                disabled={state.accountBindingsLoading}
                style={campaignDetailPageStyles.switchButton}
                onClick={() => setAccountPickerVisible(true)}
              >
                {t('forward:switchAccount')}
              </Button>
            ) : null}
          </div>

          <div style={campaignDetailPageStyles.connectionPanel}>
            <div style={campaignDetailPageStyles.connectionTopRow}>
              <div style={{ minWidth: 0 }}>
                <div style={campaignDetailPageStyles.stateTitle}>{t('detail.currentConnectionTitle')}</div>
                <div style={campaignDetailPageStyles.stateHint}>{accountStatusHint}</div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {state.connectionStateLoading ? <SpinLoading style={{ '--size': '18px' }} /> : null}
                <span style={{ ...campaignDetailPageStyles.connectionTagBase, ...connectionTag.style }}>
                  {connectionTag.text}
                </span>
              </div>
            </div>

            {(!selectedBinding || !isBindingVerified || !isConnectionReady) ? (
              <Button
                block
                style={campaignDetailPageStyles.connectButton}
                onClick={handleGoConnect}
              >
                <span style={campaignDetailPageStyles.connectButtonInner}>
                  <LinkGlyph />
                  <span>{t('detail.connectNowAction')}</span>
                </span>
              </Button>
            ) : null}
          </div>

          <div style={campaignDetailPageStyles.progressPanel}>
            <div style={campaignDetailPageStyles.progressTitle}>{t('detail.progressTitle')}</div>
            <div style={campaignDetailPageStyles.progressSummary}>
              <span>{t('detail.progressUsed', { used: usedCount, limit: promotionLimit })}</span>
              <span style={campaignDetailPageStyles.progressRemain}>{t('detail.progressRemain', { remaining: remainingCount })}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#7f879b' }}>
              {t('detail.batchSizeHint', { count: batchSize })}
            </div>
            <div style={campaignDetailPageStyles.progressTrack}>
              <div style={{ ...campaignDetailPageStyles.progressFill, width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="webview-section">
        <div style={campaignDetailPageStyles.sectionTitleRow}>
          <span style={campaignDetailPageStyles.sectionTitleBar} />
          <span style={campaignDetailPageStyles.sectionTitle}>{t('detail.batchProgressTitle')}</span>
        </div>
        <div style={campaignDetailPageStyles.batchCard}>
          {state.sendBatchesLoading ? (
            <div style={campaignDetailPageStyles.batchLoading}>
              <DotLoading />
            </div>
          ) : state.sendBatches.length === 0 ? (
            <div style={campaignDetailPageStyles.batchEmpty}>{t('detail.batchNoRecord')}</div>
          ) : (
            <div style={campaignDetailPageStyles.batchList}>
              {state.sendBatches.map((batch) => {
                const total = Math.max(batch.locked_count || batch.requested_count || 0, 1);
                const done = (batch.sent_count || 0) + (batch.failed_count || 0) + (batch.cancelled_count || 0);
                const percent = Math.max(0, Math.min(100, Math.round((done / total) * 100)));
                const canCancel = isActiveBatchStatus(batch.status);
                return (
                  <div key={batch.id} style={campaignDetailPageStyles.batchItem}>
                    <div style={campaignDetailPageStyles.batchHeader}>
                      <span style={campaignDetailPageStyles.batchStatus}>
                        {formatBatchStatus(batch, t)}
                      </span>
                      <span style={campaignDetailPageStyles.batchProgressText}>
                        {t('detail.batchProgressValue', { done, total })}
                      </span>
                    </div>
                    <div style={campaignDetailPageStyles.batchMetricGrid}>
                      <span>{t('detail.batchSentCount', { count: batch.sent_count })}</span>
                      <span>{t('detail.batchFailedCount', { count: batch.failed_count })}</span>
                      <span>{t('detail.batchCancelledCount', { count: batch.cancelled_count })}</span>
                    </div>
                    <div style={campaignDetailPageStyles.batchTrack}>
                      <div style={{ ...campaignDetailPageStyles.batchFill, width: `${percent}%` }} />
                    </div>
                    <div style={campaignDetailPageStyles.batchTimeRow}>
                      <span>{t('detail.batchStartedAt', { time: formatTime(batch.started_at || batch.created_at) })}</span>
                      {batch.completed_at || batch.cancelled_at ? (
                        <span>{t('detail.batchFinishedAt', { time: formatTime(batch.completed_at || batch.cancelled_at) })}</span>
                      ) : null}
                    </div>
                    {canCancel ? (
                      <Button
                        block
                        size="small"
                        color="danger"
                        fill="outline"
                        loading={state.cancellingBatchId === batch.id}
                        disabled={state.cancellingBatchId === batch.id}
                        style={campaignDetailPageStyles.batchCancelButton}
                        onClick={() => onCancelSendBatch(batch.id)}
                      >
                        {state.cancellingBatchId === batch.id ? t('detail.batchCancelling') : t('detail.batchCancel')}
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="webview-section">
        <div style={campaignDetailPageStyles.sectionTitleRow}>
          <span style={campaignDetailPageStyles.sectionTitleBar} />
          <span style={campaignDetailPageStyles.sectionTitle}>{t('detail.sendContentPreviewTitle')}</span>
        </div>
        {templates.length === 0 ? (
          <div style={campaignDetailPageStyles.emptySimpleCard}>{t('detail.noTemplates')}</div>
        ) : (
          <div style={campaignDetailPageStyles.templatesWrap}>
            {templates.map((template) => (
              <div key={template.key} style={campaignDetailPageStyles.sendContentCard}>
                <div style={campaignDetailPageStyles.contentBox}>
                  <div style={campaignDetailPageStyles.contentLabelBadge}>{t('detail.willSendToCustomer')}</div>
                  {template.mediaUrl ? (
                    <div style={campaignDetailPageStyles.mediaPreview}>
                      <img
                        src={normalizeMediaUrl(template.mediaUrl)}
                        alt={t('detail.sendMediaAlt')}
                        style={campaignDetailPageStyles.mediaImage}
                      />
                    </div>
                  ) : null}
                  <div style={campaignDetailPageStyles.contentText}>{template.content}</div>
                </div>
                <Button
                  block
                  color={canForward ? 'primary' : 'default'}
                  disabled={!canForward}
                  style={campaignDetailPageStyles.forwardButton}
                  onClick={() => onForward(template.content, template.mediaUrl)}
                >
                  {sendButtonText}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="webview-section">
        <div style={campaignDetailPageStyles.sectionTitleRow}>
          <span style={campaignDetailPageStyles.sectionTitleBar} />
          <span style={campaignDetailPageStyles.sectionTitle}>{t('detail.recentSendResult')}</span>
        </div>
        <div style={campaignDetailPageStyles.resultCard}>
          {state.sendReceiptsLoading ? (
            <div style={campaignDetailPageStyles.resultLoading}>
              <DotLoading />
            </div>
          ) : state.sendReceipts.length === 0 ? (
            <div style={campaignDetailPageStyles.emptyResult}>
              <div style={campaignDetailPageStyles.emptyResultIcon}>
                <WaveGlyph />
              </div>
              <div style={campaignDetailPageStyles.emptyResultTitle}>{t('detail.noSendRecord')}</div>
              <div style={campaignDetailPageStyles.emptyResultHint}>{t('detail.noSendRecordHint')}</div>
            </div>
          ) : (
            <div style={campaignDetailPageStyles.receiptList}>
              {state.sendReceipts.map((receipt) => (
                <div key={receipt.id} style={campaignDetailPageStyles.receiptCard}>
                  <div style={campaignDetailPageStyles.receiptHeader}>
                    <div style={campaignDetailPageStyles.receiptNumber}>{receipt.recipient_number}</div>
                    <span style={campaignDetailPageStyles.receiptStatus}>{receipt.status}</span>
                  </div>
                  <div style={campaignDetailPageStyles.receiptMeta}>{t('detail.submittedAt', { time: formatTime(receipt.created_at) })}</div>
                  {receipt.sent_at ? <div style={campaignDetailPageStyles.receiptMeta}>{t('detail.sentAt', { time: formatTime(receipt.sent_at) })}</div> : null}
                  {receipt.delivered_at ? <div style={campaignDetailPageStyles.receiptMeta}>{t('detail.deliveredAt', { time: formatTime(receipt.delivered_at) })}</div> : null}
                  {receipt.read_at ? <div style={campaignDetailPageStyles.receiptMeta}>{t('detail.readAt', { time: formatTime(receipt.read_at) })}</div> : null}
                  {receipt.error_message ? <div style={campaignDetailPageStyles.receiptError}>{t('detail.failureReason', { reason: receipt.error_message })}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ForwardSheet
        open={state.forwardOpen}
        content={state.forwardContent}
        mediaUrl={state.forwardMediaUrl}
        campaignId={campaign.id}
        promotionLimit={promotionLimit}
        preferredBindingId={selectedBinding?.id || undefined}
        allowAccountSwitch={false}
        hideContextCards
        onClose={onCloseForward}
        onSubmitted={onForwardSubmitted}
      />
    </div>
  );
};

function isActiveBatchStatus(status: CampaignSendBatchItem['status']): boolean {
  return status === 'queued' || status === 'sending' || status === 'cancelling';
}

function formatBatchStatus(
  batch: CampaignSendBatchItem,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  return t(`detail.batchStatus.${batch.status}`);
}

function isConnectionLoadingStatus(status: WhatsAppConnectionState['status']): boolean {
  return (
    status === 'initializing' ||
    status === 'initializing_browser' ||
    status === 'injecting_page' ||
    status === 'awaiting_qr_or_code' ||
    status === 'recovering'
  );
}

const WhatsAppBadgeGlyph: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="22" cy="22" r="22" fill="url(#paint0_linear_whatsapp_badge)" />
    <rect x="9.5" y="10" width="25" height="18" rx="6" fill="#EAF0FF" stroke="#FFFFFF" strokeWidth="1.2" />
    <rect x="12.5" y="13" width="19" height="12" rx="4" fill="#FFFFFF" />
    <path d="M27.1 16.2a4.9 4.9 0 0 0-7 0 4.86 4.86 0 0 0-1.44 3.47c0 .78.19 1.54.56 2.23l-.59 3.17 3.23-.55a4.92 4.92 0 0 0 6.74-4.85 4.86 4.86 0 0 0-1.5-3.49Z" fill="#4D66F3"/>
    <path d="M24.15 17.83c.14-.3.28-.3.4-.3.1 0 .22 0 .32.01.1 0 .24-.03.36.3.12.34.41 1.16.44 1.24.04.08.06.18.01.29-.05.11-.07.18-.14.27-.08.1-.16.2-.22.27-.08.08-.16.17-.07.34.1.16.43.71.93 1.15.65.58 1.19.76 1.35.85.16.08.26.07.35-.05.1-.12.41-.47.52-.64.11-.16.22-.14.37-.08.15.06.97.46 1.13.54.16.08.27.12.31.2.03.08.03.42-.1.84-.14.4-.8.77-1.1.82-.28.05-.65.08-1.04-.05-.25-.08-.57-.18-.98-.36-1.72-.73-2.8-2.49-2.89-2.61-.1-.13-.7-.94-.7-1.8 0-.85.44-1.28.6-1.46Z" fill="white"/>
    <defs>
      <linearGradient id="paint0_linear_whatsapp_badge" x1="6" y1="6.5" x2="38" y2="38.5" gradientUnits="userSpaceOnUse">
        <stop stopColor="#6B74F3" />
        <stop offset="1" stopColor="#8D55E8" />
      </linearGradient>
    </defs>
  </svg>
);

const LinkGlyph: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8.17 11.83 11.83 8.17M6.33 13.67l-1.16 1.16a2.59 2.59 0 1 1-3.67-3.66l2.34-2.34a2.59 2.59 0 0 1 3.66 0M13.67 6.33l1.16-1.16a2.59 2.59 0 1 1 3.67 3.66l-2.34 2.34a2.59 2.59 0 0 1-3.66 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WaveGlyph: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="22" cy="22" r="22" fill="#F5F6FB" />
    <path d="M11 24.5h4.2l2.6-7.2 4.1 13.4 4.2-11.4 2.6 5.2H33" stroke="#B8BCCA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
